<template>
  <div class="checkout-page">
    <section v-if="isCheckingCart" class="checkout-content">
      <div class="container text-center py-5">
        <div class="spinner-border text-dark" role="status"></div>
      </div>
    </section>
    <section v-else class="checkout-content">
      <div class="container">
        <h1 class="page-title">{{ t('checkout.title') }}</h1>
        
        <div class="checkout-grid">
          <div class="checkout-main">
            <!-- Guest Email Section -->
            <div v-if="isGuest" class="checkout-section mb-4">
              <div class="guest-header mb-4">
                <span class="guest-badge">{{ t('checkout.guest_badge') }}</span>
                <h2 class="section-title mb-0 border-0 pb-0">{{ t('checkout.guest_section_title') }}</h2>
              </div>
              <div class="form-section mb-3">
                <label class="form-label-premium mb-2">{{ t('checkout.guest_email') }}</label>
                <input type="email" class="form-control-premium" v-model="guestEmail" required placeholder="email@example.com" @blur="validateGuestEmail" @input="validateGuestEmail">
                <p v-if="guestFormErrors.email" class="field-error">{{ guestFormErrors.email }}</p>
              </div>
              <p class="guest-email-hint">
                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="me-1"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
                {{ t('checkout.guest_email_hint') }}
              </p>
            </div>

            <!-- Shipping Section -->
            <div v-if="isGuest" class="checkout-section mb-4">
              <h2 class="section-title">{{ t('checkout.shipping_address') }}</h2>
              <div class="row g-3">
                <div class="col-md-6">
                  <div class="form-section">
                    <label class="form-label-premium mb-2">Nome</label>
                    <input type="text" class="form-control-premium" v-model="guestShippingAddress.firstName" required @blur="validateGuestName" @input="validateGuestName">
                    <p v-if="guestFormErrors.firstName" class="field-error">{{ guestFormErrors.firstName }}</p>
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="form-section">
                    <label class="form-label-premium mb-2">Cognome</label>
                    <input type="text" class="form-control-premium" v-model="guestShippingAddress.lastName" required @blur="validateGuestName" @input="validateGuestName">
                    <p v-if="guestFormErrors.lastName" class="field-error">{{ guestFormErrors.lastName }}</p>
                  </div>
                </div>
                <div class="col-12">
                  <div class="form-section">
                    <label class="form-label-premium mb-2">Indirizzo</label>
                    <input type="text" class="form-control-premium" v-model="guestShippingAddress.street" required @blur="validateGuestAddress" @input="validateGuestAddress">
                    <p v-if="guestFormErrors.street" class="field-error">{{ guestFormErrors.street }}</p>
                  </div>
                </div>
                <div class="col-md-4">
                  <div class="form-section">
                    <label class="form-label-premium mb-2">CAP</label>
                    <input type="text" class="form-control-premium" v-model="guestShippingAddress.zipCode" required @blur="validateGuestZip" @input="validateGuestZip">
                    <p v-if="guestFormErrors.zipCode" class="field-error">{{ guestFormErrors.zipCode }}</p>
                  </div>
                </div>
                <div class="col-md-8">
                  <div class="form-section">
                    <label class="form-label-premium mb-2">Città</label>
                    <input type="text" class="form-control-premium" v-model="guestShippingAddress.city" required @blur="validateGuestAddress" @input="validateGuestAddress">
                    <p v-if="guestFormErrors.city" class="field-error">{{ guestFormErrors.city }}</p>
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="form-section">
                    <label class="form-label-premium mb-2">Provincia</label>
                    <input type="text" class="form-control-premium" v-model="guestShippingAddress.stateProvince" required @blur="validateGuestAddress" @input="validateGuestAddress">
                    <p v-if="guestFormErrors.stateProvince" class="field-error">{{ guestFormErrors.stateProvince }}</p>
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="form-section">
                    <label class="form-label-premium mb-2">Paese</label>
                    <input type="text" class="form-control-premium" v-model="guestShippingAddress.country" required @blur="validateGuestAddress" @input="validateGuestAddress">
                    <p v-if="guestFormErrors.country" class="field-error">{{ guestFormErrors.country }}</p>
                  </div>
                </div>
                <div class="col-12">
                  <div class="form-section">
                    <label class="form-label-premium mb-2">{{ t('checkout.phone') }}</label>
                    <input type="tel" class="form-control-premium" v-model="guestShippingAddress.phone" @blur="validateGuestPhone" @input="validateGuestPhone">
                    <p v-if="guestFormErrors.phone" class="field-error">{{ guestFormErrors.phone }}</p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Shipping Section per utenti autenticati -->
            <div v-else class="checkout-section mb-4">
              <h2 class="section-title">{{ t('checkout.shipping_address') }}</h2>
              <CheckoutAddressSelector v-model="selectedAddressId" />
            </div>

            <!-- Billing Section -->
            <div v-if="isGuest" class="checkout-section">
              <div class="d-flex justify-content-between align-items-center mb-3 pb-2 border-bottom">
                <h2 class="section-title mb-0 border-0 pb-0">{{ t('checkout.billing_address') }}</h2>
                <div class="form-check billing-toggle">
                  <input 
                    class="form-check-input" 
                    type="checkbox" 
                    id="billingSameAsShipping" 
                    v-model="guestBillingAddress.sameAsShipping"
                  >
                  <label class="form-check-label small fw-bold text-uppercase letter-spacing-1" for="billingSameAsShipping">
                    {{ t('checkout.same_as_shipping') }}
                  </label>
                </div>
              </div>
              
              <div v-if="!guestBillingAddress.sameAsShipping" class="mt-4">
                <div class="row g-3">
                  <div class="col-md-6">
                    <div class="form-section">
                      <label class="form-label-premium mb-2">Nome</label>
                      <input type="text" class="form-control-premium" v-model="guestBillingAddress.address.firstName" required>
                    </div>
                  </div>
                  <div class="col-md-6">
                    <div class="form-section">
                      <label class="form-label-premium mb-2">Cognome</label>
                      <input type="text" class="form-control-premium" v-model="guestBillingAddress.address.lastName" required>
                    </div>
                  </div>
                  <div class="col-12">
                    <div class="form-section">
                      <label class="form-label-premium mb-2">Indirizzo</label>
                      <input type="text" class="form-control-premium" v-model="guestBillingAddress.address.street" required>
                    </div>
                  </div>
                  <div class="col-md-4">
                    <div class="form-section">
                      <label class="form-label-premium mb-2">CAP</label>
                      <input type="text" class="form-control-premium" v-model="guestBillingAddress.address.zipCode" required>
                    </div>
                  </div>
                  <div class="col-md-8">
                    <div class="form-section">
                      <label class="form-label-premium mb-2">Città</label>
                      <input type="text" class="form-control-premium" v-model="guestBillingAddress.address.city" required>
                    </div>
                  </div>
                  <div class="col-md-6">
                    <div class="form-section">
                      <label class="form-label-premium mb-2">Provincia</label>
                      <input type="text" class="form-control-premium" v-model="guestBillingAddress.address.stateProvince" required>
                    </div>
                  </div>
                  <div class="col-md-6">
                    <div class="form-section">
                      <label class="form-label-premium mb-2">Paese</label>
                      <input type="text" class="form-control-premium" v-model="guestBillingAddress.address.country" required>
                    </div>
                  </div>
                </div>
              </div>
              <div v-else class="billing-same-preview p-3 bg-light border-dashed">
                <p class="mb-0 small text-muted">L'ordine verrà fatturato all'indirizzo di spedizione inserito.</p>
              </div>
            </div>

            <!-- Billing Section per utenti autenticati -->
            <div v-else class="checkout-section">
              <div class="d-flex justify-content-between align-items-center mb-3 pb-2 border-bottom">
                <h2 class="section-title mb-0 border-0 pb-0">{{ t('checkout.billing_address') }}</h2>
                <div class="form-check billing-toggle">
                  <input 
                    class="form-check-input" 
                    type="checkbox" 
                    id="billingSameAsShipping" 
                    v-model="billingSameAsShipping"
                  >
                  <label class="form-check-label small fw-bold text-uppercase letter-spacing-1" for="billingSameAsShipping">
                    {{ t('checkout.same_as_shipping') }}
                  </label>
                </div>
              </div>
              
              <div v-if="!billingSameAsShipping" class="mt-4">
                <CheckoutAddressSelector v-model="selectedBillingAddressId" />
              </div>
              <div v-else class="billing-same-preview p-3 bg-light border-dashed">
                <p class="mb-0 small text-muted">L'ordine verrà fatturato all'indirizzo di spedizione selezionato.</p>
              </div>
            </div>

            <!-- Notes Section -->
            <div class="checkout-section mt-4">
              <h2 class="section-title">{{ t('checkout.customer_note') || 'NOTE ORDINE (FACOLTATIVO)' }}</h2>
              <textarea 
                v-model="customerNote" 
                class="form-control customer-note-textarea" 
                rows="3"
                placeholder="Scrivi qui eventuali istruzioni per la consegna..."
                maxlength="1000"
              ></textarea>
              <div class="text-end mt-1">
                <small class="text-muted">{{ customerNote.length }}/1000</small>
              </div>
            </div>

            <!-- Payment Section (Stripe) -->
            <div v-if="orderId" class="checkout-section mt-4 payment-section shadow-sm">
              <div class="d-flex justify-content-between align-items-center mb-4 pb-2 border-bottom">
                <h2 class="section-title mb-0 border-0 pb-0">{{ t('checkout.payment_method') || 'METODO DI PAGAMENTO' }}</h2>
                <div class="secure-badge d-flex align-items-center gap-2">
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#28a745" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                  <span class="secure-text">SSL SECURE</span>
                </div>
              </div>
              
              <div class="payment-method-header mb-4">
                <p class="small text-muted mb-3 letter-spacing-1">CARTA DI CREDITO O DEBITO</p>
                <div class="payment-icons d-flex gap-3 align-items-center">
                  <img :src="visaIcon" alt="Visa" width="35" class="payment-icon">
                  <img :src="mastercardIcon" alt="Mastercard" width="32" class="payment-icon">
                  <div class="divider-v"></div>
                  <img :src="stripeIcon" alt="Stripe" width="45" class="payment-icon opacity-75">
                </div>
              </div>

              <div v-if="paymentData && isStripeReady">
                <div v-if="paymentError" class="error-message mb-3">
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="me-1"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
                  {{ paymentError }}
                </div>
                <div v-if="paymentCancelWarning" class="cancel-warning-message mb-3">
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="me-1"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
                  Pagamento annullato. Puoi scegliere un altro metodo di pagamento.
                </div>
                <StripeCheckout
                  :publishable-key="paymentData.publishableKey"
                  :client-secret="paymentData.clientSecret"
                  :order-id="orderId"
                  :total-amount="paymentData.totalAmount"
                  :merchant-name="paymentData.merchantName"
                  :email="isGuest ? guestEmail : (authStore.user?.email || '')"
                  pay-button-label="PAGA ORA"
                  @success="onPaymentSuccess"
                  @error="onPaymentError"
                  @cancel="onPaymentCancel"
                />
              </div>
              <div v-else-if="!isStripeReady && !paymentError" class="text-center py-4">
                <div class="spinner-border text-dark mb-3" role="status"></div>
                <p class="small text-muted mb-0">Caricamento modulo di pagamento...</p>
              </div>

              <div class="trust-footer mt-4 pt-3 border-top text-center">
                <p class="extra-small text-muted mb-0 letter-spacing-05">
                  I tuoi dati sono protetti da crittografia SSL a 256 bit. <br>
                  Nessun dato sensibile viene memorizzato sui nostri sistemi.
                </p>
              </div>
            </div>
          </div>

          <!-- Order Summary Sidebar -->
          <div class="checkout-sidebar">
            <div class="order-summary-box" :class="{ 'is-loading': isLoading }">
              <h2 class="summary-title">{{ t('checkout.summary') }}</h2>
              
              <div class="summary-details">
                <div v-if="localCartItems.length === 0" class="text-center py-4 text-muted">
                  <p>Il carrello è vuoto.</p>
                </div>

                <template v-else>
                  <div class="cart-items-list mb-3">
                    <div v-for="item in localCartItems" :key="item.variantId || item.id || item.cartItemId" class="cart-item-row d-flex align-items-center gap-3 mb-3 pb-3 border-bottom">
                      <img :src="getImagePath(item.image)" alt="" class="cart-item-image" width="60" height="60" style="object-fit: cover; border: 1px solid #eee; border-radius: 2px;">
                      <div class="flex-grow-1">
                        <p class="item-name mb-1 fw-bold small">{{ item.name }}</p>
                        <p class="item-variant mb-1 text-muted extra-small">{{ item.color }} {{ item.size }}</p>
                        <p class="item-qty mb-0 extra-small">Qty: {{ item.quantity }}</p>
                      </div>
                      <div class="text-end">
                        <span class="item-price fw-bold small">{{ formatPrice(item.price * item.quantity) }}</span>
                      </div>
                    </div>
                  </div>

                  <div class="summary-divider"></div>

                  <div class="summary-row">
                    <span class="label">{{ t('checkout.subtotal') }}</span>
                    <span class="value">{{ formatPrice(displaySubtotal) }}</span>
                  </div>
                  
                  <div class="summary-row">
                    <span class="label">{{ t('checkout.shipping') }}</span>
                    <span class="value">
                      <span v-if="isLoading" class="skeleton skeleton-text" style="width: 60px; display: inline-block;"></span>
                      <span v-else>{{ displayShipping === 0 ? t('header.cart.free') : formatPrice(displayShipping) }}</span>
                    </span>
                  </div>

                  <div class="summary-divider"></div>

                  <div class="summary-row total-row">
                    <span class="label">{{ t('checkout.total') }}</span>
                    <div class="total-value-container text-end">
                      <span class="value">{{ formatPrice(displayTotal) }}</span>
                      <div class="price-vat-label-checkout">{{ t('product.labels.vat_included') }}</div>
                    </div>
                  </div>

                  <div v-if="showPriceAlert" class="price-update-alert">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
                    <span>{{ t('checkout.price_update_alert') }}</span>
                  </div>

                  <button 
                    v-if="!orderId"
                    class="btn-proceed" 
                    :disabled="isLoading || isProcessingOrder || (!isGuest && (!selectedAddressId || !selectedBillingAddressId)) || (isGuest && !isGuestFormValid)"
                    @click="handlePayment"
                  >
                    <span v-if="isProcessingOrder" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    {{ isProcessingOrder ? t('checkout.processing') || 'ELABORAZIONE...' : t('checkout.proceed_btn') }}
                  </button>

                  <div v-else class="payment-active-info">
                    <p class="small text-success fw-bold mb-0">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="me-1"><polyline points="20 6 9 17 4 12"/></svg>
                      Ordine Inizializzato
                    </p>
                    <p class="extra-small text-muted mt-1">Completa il pagamento nel box a sinistra.</p>
                  </div>
                </template>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, watch, nextTick, computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useCart } from '@/composables/useCart'
import { useLanguage } from '@/composables/useLanguage'
import { useToast } from '@/composables/useToast'
import { useApiCodes } from '@/composables/useApiCodes'
import { useConfigStore } from '@/stores/configStore'
import { useAuthStore } from '@/stores/authStore'
import { checkoutService } from '@/services/checkoutService'
import { cookieService } from '@/services/cookieService'
import CheckoutAddressSelector from '@/components/CheckoutAddressSelector.vue'
import StripeCheckout from '@/components/StripeCheckout.vue'

const { subtotal: localSubtotal, cartItems: localCartItems, fetchServerCart, clearCart } = useCart()
const { t } = useLanguage()
const toast = useToast()
const router = useRouter()
const route = useRoute()
const configStore = useConfigStore()
const authStore = useAuthStore()
const apiCodes = useApiCodes()

const paymentCancelWarning = ref(!!route.query.payment_canceled)

const calculateShippingCost = (subtotal) => {
  const threshold = configStore.freeShippingThreshold || 50
  return subtotal >= threshold ? 0 : 5.99
}

watch(() => route.query.payment_canceled, (val) => {
  paymentCancelWarning.value = !!val
})

const getImagePath = (imageName) => {
  if (!imageName) return ''
  if (imageName.startsWith('http') || imageName.startsWith('//')) return imageName
  if (imageName.startsWith('/')) return imageName
  if (imageName.includes('/')) return `/${imageName}`
  return `/images/products/${imageName}`
}

const getCartFingerprint = () => {
  const items = localCartItems.value || []
  return JSON.stringify({
    count: items.length,
    subtotal: Math.round((localSubtotal.value || 0) * 100) / 100,
    items: items.map(item => `${item.variantId}:${item.quantity}`).sort().join(',')
  })
}

watch(() => localCartItems.value.length, async (newLen, oldLen) => {
  if (newLen !== oldLen) {
    await fetchSummary()
  }
})

watch(getCartFingerprint, async (newFp, oldFp) => {
  if (!oldFp) return

  if (orderId.value) {
    orderId.value = null
    guestTrackingToken.value = null
    paymentData.value = null
    isStripeReady.value = false
  }

  await fetchSummary()
  saveFormData()
})

const isGuest = computed(() => !authStore.isAuthenticated)

// Payment Icons
const visaIcon = new URL('../assets/icons/payments/visa-svgrepo-com.svg', import.meta.url).href
const mastercardIcon = new URL('../assets/icons/payments/mastercard-full-svgrepo-com.svg', import.meta.url).href
const stripeIcon = new URL('../assets/icons/payments/stripe-svgrepo-com.svg', import.meta.url).href

const summary = ref(null)
const isLoading = ref(true)
const isProcessingOrder = ref(false)
const showPriceAlert = ref(false)
const selectedAddressId = ref(null)
const selectedBillingAddressId = ref(null)
const billingSameAsShipping = ref(true)
const customerNote = ref('')

const guestEmail = ref('')
const guestShippingAddress = ref({
  firstName: '',
  lastName: '',
  street: '',
  city: '',
  zipCode: '',
  stateProvince: '',
  country: 'IT',
  phone: '',
  notes: ''
})
const guestBillingAddress = ref({
  sameAsShipping: true,
  address: null
})
const guestFormErrors = ref({
  email: '',
  firstName: '',
  lastName: '',
  street: '',
  city: '',
  zipCode: '',
  stateProvince: '',
  country: '',
  phone: ''
})

// Stripe refs
const paymentData = ref(null)
const isStripeReady = ref(false)
const paymentError = ref(null)
const orderId = ref(null)
const guestTrackingToken = ref(null)

watch([selectedAddressId, billingSameAsShipping], ([newShipId, same]) => {
  if (same) {
    selectedBillingAddressId.value = newShipId
  }
})

watch(() => guestBillingAddress.value.sameAsShipping, (same) => {
  if (same) {
    guestBillingAddress.value.address = null
  } else if (!guestBillingAddress.value.address) {
    guestBillingAddress.value.address = { ...guestShippingAddress.value }
  }
})

const STORAGE_KEY = 'checkout_form_data'

const saveFormData = () => {
  const data = {
    guestEmail: guestEmail.value,
    guestShippingAddress: guestShippingAddress.value,
    guestBillingAddress: guestBillingAddress.value,
    customerNote: customerNote.value,
    selectedAddressId: selectedAddressId.value,
    selectedBillingAddressId: selectedBillingAddressId.value,
    billingSameAsShipping: billingSameAsShipping.value,
    orderId: orderId.value,
    guestTrackingToken: guestTrackingToken.value
  }
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data))
  } catch (e) {
    console.error('[CheckoutPage] Error saving form data:', e)
  }
}

const restoreFormData = async () => {
  const raw = sessionStorage.getItem(STORAGE_KEY)
  if (!raw) return false
  try {
    const data = JSON.parse(raw)
    if (data.guestEmail !== undefined) guestEmail.value = data.guestEmail
    if (data.guestShippingAddress) guestShippingAddress.value = data.guestShippingAddress
    if (data.customerNote !== undefined) customerNote.value = data.customerNote
    if (data.selectedAddressId !== undefined) selectedAddressId.value = data.selectedAddressId
    if (data.selectedBillingAddressId !== undefined) selectedBillingAddressId.value = data.selectedBillingAddressId
    if (data.billingSameAsShipping !== undefined) billingSameAsShipping.value = data.billingSameAsShipping
    if (data.guestBillingAddress) guestBillingAddress.value = data.guestBillingAddress

    const currentFp = getCartFingerprint()
    const cartChanged = data.cartFingerprint && data.cartFingerprint !== currentFp

    if (cartChanged) {
      orderId.value = null
      guestTrackingToken.value = null
      try { sessionStorage.removeItem(STORAGE_KEY) } catch (e) { console.error('[CheckoutPage] Error clearing sessionStorage:', e) }
    } else {
      if (data.orderId) orderId.value = data.orderId
      if (data.guestTrackingToken) guestTrackingToken.value = data.guestTrackingToken
    }

    paymentData.value = null
    isStripeReady.value = false
    paymentError.value = null

    return cartChanged
  } catch (e) {
    console.error('[CheckoutPage] Error restoring form data:', e)
    return false
  }
}

const applyPaymentInitResponse = async (paymentDataRaw) => {
  const { publishableKey, clientSecret, paymentMethodTypes, merchantName } = paymentDataRaw
  if (!publishableKey || !clientSecret) {
    throw new Error("Errore: Il server non ha restituito le chiavi necessarie per il pagamento.")
  }
  paymentData.value = {
    publishableKey,
    clientSecret,
    paymentMethodTypes,
    merchantName,
    totalAmount: Math.round((summary.value?.totalAmount || 0) * 100)
  }
  await nextTick()
  isStripeReady.value = true
  paymentError.value = null

  const paymentSection = document.querySelector('.payment-section')
  if (paymentSection) {
    const navbarHeight = 180
    const elementPosition = paymentSection.getBoundingClientRect().top + window.pageYOffset
    window.scrollTo({
      top: elementPosition - navbarHeight,
      behavior: 'smooth'
    })
  }
}

const formatPrice = (price) => {
  if (price === null || price === undefined || price === '') return '0,00 €'
  const val = typeof price === 'number' ? price : parseFloat(price)
  return new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR'
  }).format(isNaN(val) ? 0 : val)
}

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const phoneRegex = /^\+?[0-9\s-]{8,20}$/
const zipRegexIT = /^\d{5}$/
const zipRegexGeneric = /^[A-Z0-9\s-]{3,10}$/i

const validateGuestEmail = () => {
  const val = guestEmail.value.trim()
  if (!val) {
    guestFormErrors.value.email = t('form.error.required')
  } else if (!emailRegex.test(val)) {
    guestFormErrors.value.email = t('form.error.email_invalid')
  } else {
    guestFormErrors.value.email = ''
  }
}

const validateGuestPhone = () => {
  const val = guestShippingAddress.value.phone.trim()
  if (!val) {
    guestFormErrors.value.phone = ''
  } else if (!phoneRegex.test(val)) {
    guestFormErrors.value.phone = t('form.error.phone_invalid')
  } else {
    guestFormErrors.value.phone = ''
  }
}

const validateGuestZip = () => {
  const val = guestShippingAddress.value.zipCode.trim()
  const country = (guestShippingAddress.value.country || '').trim().toUpperCase()
  if (!val) {
    guestFormErrors.value.zipCode = t('form.error.required')
  } else if (country === 'IT' && !zipRegexIT.test(val)) {
    guestFormErrors.value.zipCode = t('form.error.zip_invalid')
  } else if (country !== 'IT' && !zipRegexGeneric.test(val)) {
    guestFormErrors.value.zipCode = t('form.error.zip_invalid')
  } else {
    guestFormErrors.value.zipCode = ''
  }
}

const validateGuestAddress = () => {
  const a = guestShippingAddress.value
  if (!a.street || a.street.trim().length < 3) {
    guestFormErrors.value.street = t('form.error.address_min')
  } else {
    guestFormErrors.value.street = ''
  }
  if (!a.city || a.city.trim().length < 2) {
    guestFormErrors.value.city = t('form.error.city_min')
  } else {
    guestFormErrors.value.city = ''
  }
  if (!a.stateProvince || a.stateProvince.trim().length < 2) {
    guestFormErrors.value.stateProvince = t('form.error.state_min')
  } else {
    guestFormErrors.value.stateProvince = ''
  }
  if (!a.country || a.country.trim().length < 2) {
    guestFormErrors.value.country = t('form.error.country_min')
  } else {
    guestFormErrors.value.country = ''
  }
}

const validateGuestName = () => {
  const fn = guestShippingAddress.value.firstName.trim()
  const ln = guestShippingAddress.value.lastName.trim()
  if (!fn || fn.length < 2) {
    guestFormErrors.value.firstName = t('form.error.min_length', { count: 2 })
  } else {
    guestFormErrors.value.firstName = ''
  }
  if (!ln || ln.length < 2) {
    guestFormErrors.value.lastName = t('form.error.min_length', { count: 2 })
  } else {
    guestFormErrors.value.lastName = ''
  }
}

const validateAllGuestFields = () => {
  validateGuestEmail()
  validateGuestPhone()
  validateGuestZip()
  validateGuestAddress()
  validateGuestName()
}

watch([guestEmail, guestShippingAddress], () => {
  if (guestFormErrors.value.email || guestFormErrors.value.phone || guestFormErrors.value.zipCode || guestFormErrors.value.street || guestFormErrors.value.city || guestFormErrors.value.stateProvince || guestFormErrors.value.country || guestFormErrors.value.firstName || guestFormErrors.value.lastName) {
    validateAllGuestFields()
  }
}, { deep: true })

const generateRecaptchaToken = async (action = 'checkout') => {
  if (!configStore.recaptchaSiteKey) return ''
  
  const waitForRecaptcha = () => {
    return new Promise((resolve) => {
      if (window.grecaptcha && window.grecaptcha.enterprise) {
        resolve(true)
        return
      }
      
      let attempts = 0
      const interval = setInterval(() => {
        attempts++
        if (window.grecaptcha && window.grecaptcha.enterprise) {
          clearInterval(interval)
          resolve(true)
        } else if (attempts > 50) {
          clearInterval(interval)
          resolve(false)
        }
      }, 100)
    })
  }

  const isRecaptchaReady = await waitForRecaptcha()
  if (!isRecaptchaReady) return ''

  return await new Promise((resolve) => {
    window.grecaptcha.enterprise.ready(async () => {
      try {
        const token = await window.grecaptcha.enterprise.execute(configStore.recaptchaSiteKey, { action })
        resolve(token)
      } catch (e) {
        console.error('[RECAPTCHA] Execute error:', e)
        resolve('')
      }
    })
  })
}

const fetchSummary = async () => {
  isLoading.value = true
  showPriceAlert.value = false
  try {
    if (isGuest.value) {
      const items = localCartItems.value || []
      const subtotal = Math.round(items.reduce((sum, item) => sum + (Number(item.price || 0) * Number(item.quantity || 0)), 0) * 100) / 100
      const shippingCost = calculateShippingCost(subtotal)
      summary.value = {
        subtotal,
        shippingCost,
        totalAmount: subtotal + shippingCost,
        discount: 0
      }
    } else {
      const data = await checkoutService.getCheckoutSummary()
      const actualData = data?.data || data
      summary.value = actualData
      const serverSubtotal = Math.round(Number(actualData.subtotal || 0) * 100) / 100
      const previousSubtotal = Math.round(Number(localSubtotal.value || 0) * 100) / 100
      if (previousSubtotal > 0 && Math.abs(serverSubtotal - previousSubtotal) > 0.01) {
        showPriceAlert.value = true
      }
    }
  } catch (error) {
    const errorCode = error?.response?.data?.code
    if (errorCode === 'CHECKOUT_EMPTY_CART') {
      const message = error.response.data.message || t('apiCodes.CHECKOUT_EMPTY_CART')
      toast.info(message)
      router.replace({ name: 'Cart' })
      return
    }
    console.error('[CheckoutPage] Error:', error)
    toast.error(t('toast.error.generic'))
  } finally {
    isLoading.value = false
  }
}

const handlePayment = async () => {
  if (localCartItems.value.length === 0) {
    paymentError.value = t('toast.error.generic')
    return
  }

  if (isGuest.value) {
    validateAllGuestFields()
    const a = guestShippingAddress.value || {}
    const phoneOk = !a.phone || a.phone.trim().length >= 8
    const hasErrors = Object.values(guestFormErrors.value).some(e => e !== '') ||
      !guestEmail.value.trim() ||
      !phoneOk
    
    if (hasErrors) {
      paymentError.value = t('form.error.required')
      return
    }
  } else {
    if (!selectedAddressId.value || !selectedBillingAddressId.value) {
      paymentError.value = t('form.error.required')
      return
    }
  }

  isProcessingOrder.value = true
  paymentError.value = null
  
  try {
    const recaptchaTokenDraft = await generateRecaptchaToken('checkout')
    if (!recaptchaTokenDraft) {
      throw new Error("Verifica di sicurezza (reCAPTCHA) non disponibile. Ricarica la pagina. Se il problema persiste, contatta l'assistenza.")
    }

    const cartItems = isGuest.value ? localCartItems.value.map(item => ({
      variantId: item.variantId,
      quantity: item.quantity
    })) : []

    const draftData = isGuest.value ? {
      isGuest: true,
      customerEmail: guestEmail.value,
      shippingAddress: guestShippingAddress.value,
      billingAddress: guestBillingAddress.value,
      cartItems,
      customerNote: customerNote.value.trim() || null
    } : {
      shippingAddressId: selectedAddressId.value,
      billingAddressId: billingSameAsShipping.value ? selectedAddressId.value : selectedBillingAddressId.value,
      customerNote: customerNote.value.trim() || null
    }

    const draftResponse = await checkoutService.createDraftOrder(draftData, recaptchaTokenDraft)
    const rawDraftData = draftResponse.data || draftResponse.item || draftResponse
    orderId.value = rawDraftData.orderId || rawDraftData.salesOrderId || rawDraftData.id || rawDraftData.SalesOrderId || draftResponse.orderId
    guestTrackingToken.value = rawDraftData.guestTrackingToken || null
    
    if (!orderId.value) {
      throw new Error("Errore: Impossibile recuperare l'ID dell'ordine dal server.")
    }

    if (isGuest.value) {
      const items = localCartItems.value || []
      const subtotal = Math.round(items.reduce((sum, item) => sum + (Number(item.price || 0) * Number(item.quantity || 0)), 0) * 100) / 100
      const draftTotal = rawDraftData.totalAmount || rawDraftData.TotalAmount || subtotal
      const shippingCost = calculateShippingCost(subtotal)
      summary.value = {
        subtotal,
        shippingCost: shippingCost >= 0 ? shippingCost : 0,
        totalAmount: Math.round((draftTotal || 0) * 100) / 100,
        discount: rawDraftData.discount || rawDraftData.Discount || 0
      }
    }

    const recaptchaTokenPayment = await generateRecaptchaToken('payment')
    if (!recaptchaTokenPayment) {
      throw new Error("Verifica di sicurezza (reCAPTCHA) non disponibile. Ricarica la pagina. Se il problema persiste, contatta l'assistenza.")
    }

    const paymentInitResponse = await checkoutService.initializePayment(orderId.value, guestTrackingToken.value, recaptchaTokenPayment)
    const paymentDataRaw = paymentInitResponse.data || paymentInitResponse

    await applyPaymentInitResponse(paymentDataRaw)
    saveFormData()

  } catch (error) {
    let errorMessage = t('toast.error.generic')
    const backendMessage = error.response?.data?.message || error.response?.data?.error || error.response?.data?.title
    const backendCode = error.response?.data?.code
    
    console.error('[CheckoutPage] Errore backend:', {
      status: error.response?.status,
      data: error.response?.data,
      message: error.message,
      code: backendCode
    })
    
    if (backendCode === 'INSUFFICIENT_STOCK' && Array.isArray(error.response?.data?.errors)) {
      const itemLines = error.response.data.errors.map(err =>
        `${err.productName} (${err.size} / ${err.colorName}): richiesti ${err.requestedQuantity}, disponibili ${err.availableQuantity}`
      )
      errorMessage = `${t('checkout.insufficient_stock_title')}\n${t('checkout.insufficient_stock_message')}\n${itemLines.join('\n')}`
    } else if (error.response?.status === 400 && backendMessage) {
      if (String(backendMessage).includes('Verifica di sicurezza')) {
        errorMessage = t('toast.error.generic')
      } else {
        errorMessage = backendMessage
      }
    } else if (backendMessage) {
      errorMessage = backendMessage
    } else if (error.message) {
      errorMessage = error.message
    }
    
    if (backendCode) {
      if (backendCode !== 'INSUFFICIENT_STOCK') {
        const friendlyCode = apiCodes.getErrorMessage(backendCode)
        if (friendlyCode && friendlyCode !== backendCode) {
          errorMessage = friendlyCode
        }
      }
    }
    
    paymentError.value = errorMessage
    toast.error(errorMessage)
  } finally {
    isProcessingOrder.value = false
  }
}

const resumePaymentAfterCancel = async () => {
  if (!orderId.value) return

  isProcessingOrder.value = true
  paymentError.value = null

  try {
    const recaptchaTokenPayment = await generateRecaptchaToken('payment')
    if (!recaptchaTokenPayment) {
      throw new Error("Verifica di sicurezza (reCAPTCHA) non disponibile. Ricarica la pagina. Se il problema persiste, contatta l'assistenza.")
    }

    const paymentInitResponse = await checkoutService.initializePayment(orderId.value, guestTrackingToken.value, recaptchaTokenPayment)
    const paymentDataRaw = paymentInitResponse.data || paymentInitResponse

    await applyPaymentInitResponse(paymentDataRaw)
    saveFormData()
  } catch (error) {
    let errorMessage = t('toast.error.generic')
    const backendMessage = error.response?.data?.message || error.response?.data?.error || error.response?.data?.title
    const backendCode = error.response?.data?.code

    console.error('[CheckoutPage] Errore backend:', {
      status: error.response?.status,
      data: error.response?.data,
      message: error.message,
      code: backendCode
    })

    const isOrderNotPending = backendCode === 'PAYMENT_ORDER_NOT_PENDING' || String(backendMessage || '').includes('PAYMENT_ORDER_NOT_PENDING')

    if (error.response?.status === 400 && backendMessage) {
      if (String(backendMessage).includes('Verifica di sicurezza')) {
        errorMessage = t('toast.error.generic')
      } else {
        errorMessage = backendMessage
      }
    } else if (backendMessage) {
      errorMessage = backendMessage
    } else if (error.message) {
      errorMessage = error.message
    }

    if (backendCode) {
      if (backendCode !== 'INSUFFICIENT_STOCK') {
        const friendlyCode = apiCodes.getErrorMessage(backendCode)
        if (friendlyCode && friendlyCode !== backendCode) {
          errorMessage = friendlyCode
        }
      }
    }

    if (isOrderNotPending) {
      orderId.value = null
      guestTrackingToken.value = null
      paymentData.value = null
      isStripeReady.value = false
      paymentCancelWarning.value = false

      try { sessionStorage.removeItem(STORAGE_KEY) } catch (e) { console.error('[CheckoutPage] Error clearing sessionStorage:', e) }

      toast.error(errorMessage || t('toast.error.generic'))
      await handlePayment()
      return
    }

    paymentError.value = errorMessage
    toast.error(errorMessage)
  } finally {
    isProcessingOrder.value = false
  }
}

const isGuestFormValid = computed(() => {
  if (!isGuest.value) return true
  validateAllGuestFields()
  const a = guestShippingAddress.value || {}
  const b = guestBillingAddress.value || {}
  const phoneOk = !a.phone || a.phone.trim().length >= 8
  return !!guestEmail.value && emailRegex.test(guestEmail.value.trim()) &&
    !!a.firstName && a.firstName.trim().length >= 2 &&
    !!a.lastName && a.lastName.trim().length >= 2 &&
    !!a.street && a.street.trim().length >= 3 &&
    !!a.city && a.city.trim().length >= 2 &&
    !!a.zipCode && a.zipCode.trim().length > 0 &&
    !!a.stateProvince && a.stateProvince.trim().length >= 2 &&
    !!a.country && a.country.trim().length >= 2 &&
    phoneOk &&
    (b.sameAsShipping || (b.address && !!b.address.firstName && !!b.address.lastName && !!b.address.street && !!b.address.city && !!b.address.zipCode && !!b.address.stateProvince && !!b.address.country))
})

const displaySubtotal = computed(() => (summary.value && summary.value.subtotal !== undefined) ? summary.value.subtotal : (localSubtotal.value || 0))
const displayShipping = computed(() => {
  const subtotal = displaySubtotal.value || 0
  return calculateShippingCost(subtotal)
})
const displayTotal = computed(() => {
  const subtotal = displaySubtotal.value || 0
  const shipping = displayShipping.value || 0
  return Math.round((subtotal + shipping) * 100) / 100
})

const onPaymentSuccess = async ({ orderId: id }) => {
  const query = { orderId: id }
  if (guestTrackingToken.value) {
    query.guestTrackingToken = guestTrackingToken.value
  }
  router.push({ name: 'CheckoutSuccess', query })
}

  const onPaymentError = (err) => {
    console.error('[CheckoutPage] Payment error:', err)
    paymentError.value = err?.message || 'Pagamento non completato. Riprova.'
  }

  const onPaymentCancel = () => {
    paymentCancelWarning.value = true
  }

const initCheckout = async () => {
  await restoreFormData()
  await fetchSummary()
}

const isCheckingCart = ref(true)
const cartIsEmpty = ref(false)

onMounted(async () => {
  if (authStore.isAuthenticated) {
    try {
      await fetchServerCart()
    } catch (err) {
      console.error('[CheckoutPage] Error fetching server cart on mount:', err)
    }
  }

  await initCheckout()
  isCheckingCart.value = false

  if (localCartItems.value.length === 0) {
    cartIsEmpty.value = true
    toast.error('Il carrello è vuoto. Aggiungi prodotti prima di procedere al checkout.')
    router.replace({ name: 'Cart' })
    return
  }

  if (route.query.payment_canceled || orderId.value) {
    await resumePaymentAfterCancel()
  }

  // Meta Pixel: InitiateCheckout
  cookieService.trackPixelEvent('InitiateCheckout', {
    value: displayTotal.value || localSubtotal.value,
    currency: 'EUR',
    content_ids: localCartItems.value.map(item => String(item.categoryTypeSku || item.sku || item.id || item.productId || item.variantId)).filter(Boolean),
    num_items: localCartItems.value.reduce((acc, item) => acc + item.quantity, 0)
  })

  // TikTok Pixel: InitiateCheckout
  cookieService.trackTikTokEvent('InitiateCheckout', {
    content_ids: localCartItems.value.map(item => String(item.categoryTypeSku || item.sku || item.id || item.productId || item.variantId)).filter(Boolean),
    contents: localCartItems.value.map(item => ({
      content_id: String(item.categoryTypeSku || item.sku || item.id || item.productId || item.variantId),
      content_type: 'product',
      content_name: item.name,
      price: item.price,
      quantity: item.quantity
    })).filter(item => item.content_id),
    content_type: 'product',
    value: displayTotal.value || localSubtotal.value,
    currency: 'EUR'
  })

  // GA4: begin_checkout
  cookieService.trackGA4EcommerceEvent('begin_checkout', {
    currency: 'EUR',
    value: displayTotal.value || localSubtotal.value,
    items: localCartItems.value.map(item => ({
      item_id: String(item.categoryTypeSku || item.sku || item.id || item.productId || item.variantId),
      item_name: item.name,
      price: item.price,
      quantity: item.quantity
    })).filter(item => item.item_id)
  })
})
onUnmounted(saveFormData)
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap');

.checkout-page {
  background: #f8f8f8;
  min-height: 80vh;
  font-family: 'Inter', sans-serif;
}

.checkout-content {
  padding: 60px 0;
}

.page-title {
  font-family: 'Inter', sans-serif;
  font-size: 18px;
  font-weight: 700;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: #000;
  margin-bottom: 40px;
  text-align: center;
}

.guest-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 25px;
}

.guest-badge {
  font-family: 'Inter', sans-serif;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  color: #28a745;
  background: #f0fff4;
  padding: 6px 12px;
  border-radius: 4px;
  border: 1px solid #c3e6cb;
}

.field-error {
  font-family: 'Inter', sans-serif;
  font-size: 11px;
  color: #fa755a;
  margin: 4px 0 0 0;
  font-weight: 500;
}

.checkout-grid {
  display: grid;
  grid-template-columns: 1fr 380px;
  gap: 30px;
}

@media (max-width: 991px) {
  .checkout-grid {
    grid-template-columns: 1fr;
  }
}

.checkout-section {
  background: #fff;
  padding: 30px;
  border: 1px solid #eee;
  border-radius: 2px;
  margin-bottom: 20px;
}

.section-title {
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  color: #000;
  margin-bottom: 20px;
  padding-bottom: 12px;
  border-bottom: 1px solid #eee;
}

.address-placeholder {
  padding: 40px 0;
  text-align: center;
  color: #888;
  font-size: 14px;
}

.secure-badge {
  background: #f0fff4;
  padding: 4px 10px;
  border-radius: 4px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.secure-text {
  font-family: 'Inter', sans-serif;
  font-size: 9px;
  font-weight: 800;
  color: #28a745;
  letter-spacing: 1px;
}

.payment-icon {
  transition: all 0.3s ease;
  opacity: 1;
}

.payment-section:hover .payment-icon {
  transform: scale(1.05);
}

.divider-v {
  width: 1px;
  height: 20px;
  background: #eee;
}

.form-label-premium {
  font-family: 'Inter', sans-serif;
  display: block;
  font-size: 10px;
  font-weight: 700;
  color: #111;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  margin-bottom: 8px;
}

.form-control-premium {
  font-family: 'Inter', sans-serif;
  width: 100%;
  border: 1px solid #e0e0e0;
  padding: 14px;
  font-size: 14px;
  border-radius: 0;
  transition: all 0.2s;
  background: #fff;
}

.form-control-premium:focus {
  outline: none;
  border-color: #000;
  box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.05);
}

.stripe-card-input {
  background: #fff;
  border: 1px solid #e0e0e0 !important;
  transition: all 0.2s ease;
  border-radius: 0;
  padding: 12px !important;
}

.stripe-card-input.StripeElement--focus {
  border-color: #000 !important;
  box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.05);
}

.error-message {
  background: #fff5f5;
  border-left: 3px solid #fa755a;
  padding: 12px 16px;
  border-radius: 0 4px 4px 0;
  animation: shake 0.5s cubic-bezier(.36,.07,.19,.97) both;
  display: flex;
  align-items: center;
  box-shadow: 0 2px 4px rgba(250, 117, 90, 0.1);
  font-size: 13px;
  color: #fa755a;
}

.cancel-warning-message {
  background: #fffdf0;
  border-left: 3px solid #ffc107;
  padding: 12px 16px;
  border-radius: 0 4px 4px 0;
  display: flex;
  align-items: center;
  box-shadow: 0 2px 4px rgba(255, 193, 7, 0.1);
  font-size: 13px;
  color: #856404;
}

@keyframes shake {
  10%, 90% { transform: translate3d(-1px, 0, 0); }
  20%, 80% { transform: translate3d(2px, 0, 0); }
  30%, 50%, 70% { transform: translate3d(-4px, 0, 0); }
  40%, 60% { transform: translate3d(4px, 0, 0); }
}

.payment-section {
  animation: fadeIn 0.6s cubic-bezier(0.165, 0.84, 0.44, 1);
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

.order-summary-box {
  background: #fff;
  padding: 30px;
  border: 1px solid #eee;
  border-radius: 2px;
  position: sticky;
  top: 100px;
}

.summary-title {
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  color: #000;
  margin-bottom: 25px;
  padding-bottom: 15px;
  border-bottom: 1px solid #eee;
}

.summary-row {
  display: flex;
  justify-content: space-between;
  margin-bottom: 15px;
  font-family: 'Inter', sans-serif;
}

.summary-row .label {
  font-size: 12px;
  color: #666;
  font-weight: 500;
}

.summary-row .value {
  font-size: 12px;
  font-weight: 700;
  color: #000;
}

.summary-divider {
  height: 1px;
  background: #eee;
  margin: 20px 0;
}

.total-row {
  margin-top: 10px;
}

.total-row .label {
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 800;
  color: #000;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.total-row .value {
  font-family: 'Inter', sans-serif;
  font-size: 18px;
  font-weight: 900;
  color: #000;
  letter-spacing: -0.5px;
}

.total-value-container {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

.price-vat-label-checkout {
  font-family: 'Inter', sans-serif;
  font-size: 9px;
  color: #888;
  font-weight: 400;
  margin-top: 2px;
  letter-spacing: 0.5px;
  text-transform: none;
}

.btn-proceed {
  font-family: 'Inter', sans-serif;
  width: 100%;
  background: #000;
  color: #fff;
  border: none;
  padding: 18px;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 2px;
  text-transform: uppercase;
  margin-top: 25px;
  transition: all 0.3s cubic-bezier(0.165, 0.84, 0.44, 1);
  border-radius: 0;
}

.btn-proceed:hover:not(:disabled) {
  background: #222;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.btn-proceed:disabled {
  background: #e0e0e0;
  color: #a0a0a0;
  cursor: not-allowed;
  box-shadow: none;
  transform: none;
}

.btn-pay-now-premium {
  font-family: 'Inter', sans-serif;
  background: #000;
  color: #fff;
  border: none;
  padding: 20px;
  font-size: 12px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 2px;
  transition: all 0.3s cubic-bezier(0.165, 0.84, 0.44, 1);
  border-radius: 0;
  position: relative;
  overflow: hidden;
}

.btn-pay-now-premium:hover:not(:disabled) {
  background: #222;
  transform: translateY(-2px);
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
}

.btn-pay-now-premium:disabled {
  background: #e0e0e0;
  color: #a0a0a0;
  cursor: not-allowed;
  box-shadow: none;
  transform: none;
}

.extra-small {
  font-family: 'Inter', sans-serif;
  font-size: 10px;
  line-height: 1.6;
  color: #888;
}

.letter-spacing-1 { letter-spacing: 1px; }
.letter-spacing-2 { letter-spacing: 2px; }
.letter-spacing-05 { letter-spacing: 0.5px; }

.billing-same-preview {
  background: #f8f8f8;
  border: 1px dashed #ddd;
  padding: 15px;
  border-radius: 2px;
}

.billing-same-preview p {
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  color: #666;
  margin: 0;
}

.customer-note-textarea {
  font-family: 'Inter', sans-serif;
  border: 1px solid #e0e0e0;
  border-radius: 0;
  padding: 14px;
  font-size: 14px;
  resize: vertical;
  transition: all 0.2s;
}

.customer-note-textarea:focus {
  outline: none;
  border-color: #000;
  box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.05);
}

.price-update-alert {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px;
  background: #fff8e1;
  border: 1px solid #ffecb3;
  border-radius: 2px;
  margin-top: 15px;
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  color: #856404;
}

.payment-active-info {
  text-align: center;
  padding: 20px;
  background: #f8f8f8;
  border-radius: 2px;
  border: 1px solid #eee;
}

.summary-error {
  text-align: center;
  padding: 40px 20px;
  color: #888;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
}

.payment-request-button {
  margin-bottom: 20px;
}

.cart-items-list {
  font-family: 'Inter', sans-serif;
}

.cart-item-row {
  border-bottom: 1px solid #eee;
}

.cart-item-image {
  border: 1px solid #eee;
  border-radius: 2px;
  background: #fff;
}

.item-name {
  font-size: 13px;
  color: #000;
  margin: 0;
}

.item-variant {
  font-size: 11px;
  color: #888;
  margin: 0;
}

.item-qty {
  font-size: 11px;
  color: #666;
  margin: 0;
}

.item-price {
  font-size: 13px;
  color: #000;
}

.guest-email-hint {
  font-family: 'Inter', sans-serif;
  font-size: 11px;
  line-height: 1.6;
  color: #888;
  margin: 0;
  display: flex;
  align-items: flex-start;
  gap: 6px;
}

.guest-email-hint svg {
  flex-shrink: 0;
  margin-top: 2px;
  opacity: 0.6;
}

@media (max-width: 768px) {
  .checkout-content {
    padding: 40px 0;
  }

  .page-title {
    font-size: 16px;
    margin-bottom: 30px;
  }

  .checkout-section {
    padding: 20px;
  }

  .order-summary-box {
    position: static;
    margin-top: 20px;
  }

  .form-control-premium {
    padding: 12px;
    font-size: 14px;
  }

  .order-item {
    grid-template-columns: 80px 1fr;
    column-gap: 14px;
    row-gap: 8px;
    padding: 16px 0;
  }

  .order-item-image {
    width: 80px;
    height: 80px;
  }

  .order-item-name {
    font-size: 13px;
  }

  .recaptcha-notice {
    font-size: 9px;
  }
}

@media (max-width: 576px) {
  .checkout-content {
    padding: 24px 0;
  }
  .checkout-section {
    padding: 16px 14px;
    margin-bottom: 14px;
  }
  .section-title {
    font-size: 11px;
    margin-bottom: 14px;
  }
  .order-summary-box {
    padding: 18px 16px !important;
  }
  .form-row {
    grid-template-columns: 1fr !important;
    gap: 14px !important;
  }
  .form-control-premium {
    padding: 11px 12px;
    font-size: 14px;
  }
  .form-label-premium {
    font-size: 9px;
    margin-bottom: 6px;
  }
  .btn-premium {
    padding: 13px 18px;
    font-size: 11px;
  }
  .summary-row .label {
    font-size: 11px;
  }
  .summary-row .value {
    font-size: 12px;
  }
  .summary-total {
    font-size: 14px !important;
  }
  .order-item-image {
    width: 64px;
    height: 64px;
  }
}

@media (max-width: 380px) {
  .checkout-section {
    padding: 14px 12px;
  }
  .order-summary-box {
    padding: 14px 12px !important;
  }
}
</style>